import { Container, Sprite } from "pixi.js"
import { emberEquipmentOf } from "game-common/ember_sprite/generate"
import { emberSpriteFor } from "game-common/ember_sprite/humanoids"
import { Entity, EntityId } from "game-common/models"
import { emberFrameBaker } from "./ember_frame_baker"
import { LpcLayered } from "./lpc_layered"

/**
 * Where an LPC paperdoll's head sits from its origin: its 64px frame is centred there and the LPC
 * head is drawn around row 22. Widgets frame their crops on it (the roster cards, the run summary).
 */
export const LPC_PORTRAIT_HEAD = { x: 0, y: -10 }

/**
 * An Ember body is drawn at true proportions -- its head a third the size of LPC's big one -- so
 * a portrait zooms in on it to fill the same head-and-shoulders crop.
 */
export const EMBER_PORTRAIT_ZOOM = 1.9

/** What a widget needs of a still portrait, whichever body draws it. */
export type Paperdoll = Container & { isReadyToShow?: () => boolean; updatePaperdoll: (entity: Entity) => void }

/**
 * A still, front-facing portrait of an Ember body: the entity's first idle frame, from the same
 * baked frames its sprite in the world uses, dressed in its equipment, placed so its head lands
 * where an LPC paperdoll's does -- a drop-in for LpcLayered in a widget's crop.
 */
export class EmberPortrait extends Container {
    private sprite?: Sprite
    private released = false

    constructor(readonly entityId: EntityId) {
        super()
    }

    updatePaperdoll(entity: Entity): void {
        const name = emberSpriteFor(entity?.appearance)
        if (!name) return
        emberFrameBaker.request(
            name,
            "idle-front",
            true,
            baked => {
                if (!baked || this.released) return
                this.sprite?.destroy()
                const sprite = new Sprite(baked.textures[0])
                const head = baked.joints[0].head
                sprite.scale.set(EMBER_PORTRAIT_ZOOM)
                sprite.position.set(LPC_PORTRAIT_HEAD.x - head.x * EMBER_PORTRAIT_ZOOM, LPC_PORTRAIT_HEAD.y - head.y * EMBER_PORTRAIT_ZOOM)
                this.addChild(sprite)
                this.sprite = sprite
            },
            () => this.released,
            emberEquipmentOf(entity),
        )
    }

    isReadyToShow(): boolean {
        return !!this.sprite && !this.released
    }

    remove(): void {
        this.parent?.removeChild(this)
        this.destroy({ children: true })
    }

    destroy(options?: Parameters<Container["destroy"]>[0]): void {
        // The frames are the baker's, shared with every sprite of this body: never the textures.
        this.released = true
        super.destroy(typeof options === "object" ? { ...options, texture: false, baseTexture: false } : options)
    }
}

/**
 * A still portrait of an entity for a widget: its Ember body where it has one (emberSpriteFor),
 * else its LPC paperdoll. `staticPose` asks the LPC one for a single frame, as the Ember one is.
 */
export const paperdollFor = (entity: Entity, entityId: EntityId, staticPose = false): Paperdoll => {
    const portrait: Paperdoll = emberSpriteFor(entity?.appearance) ? new EmberPortrait(entityId) : new LpcLayered(entityId, staticPose)
    portrait.updatePaperdoll(entity)
    return portrait
}
